import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "commons/store/store";
import axiosSecureInstance from "commons/axios/axiosSecureInstance";
import {
  ElementResourceTransferElementResourceTransfersStatusIdRead,
  ElementResourceTransferElementResourceTransfersViewIdRead,
} from "types/api.types";
import { downloadBlobFile } from "utils/files/downloadBlobFile";

interface TransferFile {
  data: any;
  status: ElementResourceTransferElementResourceTransfersStatusIdRead;
  viewLink: ElementResourceTransferElementResourceTransfersViewIdRead;
  name: any;
  randomize: any;
  isDownloading: boolean;
  isLoading: boolean;
  error: any;
}

const initialState: TransferFile = {
  data: [],
  status: {},
  viewLink: {},
  name: [],
  randomize: {},
  isDownloading: false,
  isLoading: false,
  error: null
};

type updateElementParams = {
  elementId: string;
  surveyId: string;
};

export const transferFile = createAsyncThunk(
  "transfer/file",
  async ({ elementId, surveyId }: updateElementParams, thunkAPI) => {
    try {
      const response = await axiosSecureInstance.put<any>(
        `/api/element_datas/${elementId}/transfer`,
        {
          survey: `/api/ecrves/${surveyId}`,
        }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const transferDownloadFile = createAsyncThunk(
  "transfer/downloadFile",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosSecureInstance.get(`/api/element_resource_transfers/${id}/download`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];

      const now = new Date();
      const formattedDate = now.toISOString().replace(/\D/g, "").slice(0, 14);

      let fileName = `tf_${formattedDate}.xlsx`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/);
        if (match && match[1]) {
          fileName = decodeURIComponent(match[1]);
        }
      }

      downloadBlobFile(new Blob([response.data]), fileName);

      return "File downloaded successfully";
    } catch (error: any) {
      console.error('Error exporting file:', error);
      return rejectWithValue(error.response?.data || "Failed to export file");
    }
  }
);

export const transferDownloadFiles = createAsyncThunk(
  "transfer/downloadFiles",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosSecureInstance.get(`/api/element_datas/${id}/download`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];

      const now = new Date();
      const formattedDate = now.toISOString().replace(/\D/g, "").slice(0, 14);

      let fileName = `tf_${formattedDate}.xlsx`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/);
        if (match && match[1]) {
          fileName = decodeURIComponent(match[1]);
        }
      }

      downloadBlobFile(new Blob([response.data]), fileName);

      return "File downloaded successfully";
    } catch (error: any) {
      console.error('Error exporting file:', error);
      return rejectWithValue(error.response?.data || "Failed to export file");
    }
  }
);

export const transferName = createAsyncThunk(
  "transfer/name", async (viewUrl: string, thunkAPI) => {
  try {
    const response = await axiosSecureInstance.get<any>(viewUrl);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const transferStatus = createAsyncThunk(
  "transfer/status",
  async (statusUrl: string, thunkAPI) => {
    try {
      const response =
        await axiosSecureInstance.get<ElementResourceTransferElementResourceTransfersStatusIdRead>(
          statusUrl
        );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const transferViewLink = createAsyncThunk(
  "transfer/viewLink",
  async (url: string, thunkAPI) => {
    try {
      const response =
        await axiosSecureInstance.get<ElementResourceTransferElementResourceTransfersViewIdRead>(
          url
        );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const elementResourceTransferSlice = createSlice({
  name: 'elementResourceTransfer',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(transferFile.fulfilled, (state, action) => {
      state.data = action.payload;
    });
    builder
      .addCase(transferDownloadFile.pending, (state) => {
        state.isDownloading = true;
        state.error = null;
      })
      .addCase(transferDownloadFile.fulfilled, (state) => {
        state.isDownloading = false;
      })
      .addCase(transferDownloadFile.rejected, (state, action) => {
        state.isDownloading = false;
        state.error = action.payload;
      });
    builder
      .addCase(transferDownloadFiles.pending, (state) => {
        state.isDownloading = true;
        state.error = null;
      })
      .addCase(transferDownloadFiles.fulfilled, (state) => {
        state.isDownloading = false;
      })
      .addCase(transferDownloadFiles.rejected, (state, action) => {
        state.isDownloading = false;
        state.error = action.payload;
      });
    builder.addCase(transferName.fulfilled, (state, action) => {
      state.name = action.payload;
    });
    builder.addCase(transferStatus.fulfilled, (state, action) => {
      state.status = action.payload;
    });
    builder.addCase(transferViewLink.fulfilled, (state, action) => {
      state.viewLink = action.payload;
    });
  },
});

export const selectTransferFile = (state: RootState) => state.survey.elementResourceTransfer.data;
export const selectTransferFileDownloading = (state: RootState) => state.survey.elementResourceTransfer.isDownloading;
export const selectTransferFileName = (state: RootState) => state.survey.elementResourceTransfer.name;
export const selectTransferFileStatus = (state: RootState) => state.survey.elementResourceTransfer.status;
export const selectTransferFileViewLink = (state: RootState) => state.survey.elementResourceTransfer.viewLink;

export default elementResourceTransferSlice.reducer;
